import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useState } from 'react';
import { DropdownButton } from './DropdownButton.client';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { SupabaseLogo } from '~/components/ui/logos/SupabaseLogo';
import { deleteSupabaseProjectIntegration } from '~/lib/.client/api/supabase';
import { projectStore } from '~/lib/stores/project';
import { supabaseStore } from '~/lib/stores/supabase';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { invariant } from '~/utils/invariant';

export const SupabaseButton = memo(() => {
  const isConfigVisible = useStore(supabaseStore.isConfigVisible);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const supabaseProject = useStore(supabaseStore.supabaseProject);
  const projectId = useStore(projectStore.projectId);
  const supabaseStatus = useStore(supabaseStore.supabaseStatus);

  const supabaseIntegration = useStore(supabaseStore.supabaseProjectIntegration);
  const isBoltNative = supabaseIntegration?.isBoltNative;

  const onToggleDropdown = useCallback(() => {
    supabaseStore.isConfigVisible.set(!isConfigVisible);
  }, [isConfigVisible]);

  const onClickGoToSupabase = () => {
    invariant(supabaseIntegration);

    const {
      project: { id: supabaseProjectId },
    } = supabaseIntegration;

    window.open(`https://supabase.com/dashboard/project/${supabaseProjectId}`);

    return;
  };

  async function onDisconnect() {
    invariant(projectId);

    supabaseStore.supabaseProjectIntegration.set(undefined);
    supabaseStore.supabaseStatus.set('NOT_CONFIGURED');
    supabaseStore.isConfigVisible.set(false);

    await deleteSupabaseProjectIntegration(projectId);

    setIsDisconnectDialogOpen(false);
  }

  if (supabaseStatus !== 'CONFIGURED' || isBoltNative) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Root open={isConfigVisible} modal={false} onOpenChange={onToggleDropdown}>
        <DropdownMenu.Trigger asChild>
          <LegacyIconButton className="relative" size="sm">
            <SupabaseLogo size="sm" />
          </LegacyIconButton>
        </DropdownMenu.Trigger>
        <AnimatePresence>
          {isConfigVisible && (
            <DropdownMenu.Portal forceMount>
              <DropdownMenu.Content
                className="z-visibility-dropdown"
                asChild
                forceMount
                avoidCollisions={false}
                sideOffset={3}
                onFocusOutside={() => supabaseStore.isConfigVisible.set(false)}
                align="end"
              >
                <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                  <div className="text-xs bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 p-2 border border-bolt-elements-borderColor rounded-md select-none flex flex-col gap-1">
                    <div className="flex flex-col gap-0.5 rounded-md bg-bolt-elements-background-depth-4 px-3 py-2">
                      <div className="flex items-center justify-between">
                        <div className="w-40">
                          <div className="truncate">{supabaseProject?.name}</div>
                          <span className="text-left text-bolt-elements-text-secondary">{supabaseProject?.region}</span>
                        </div>
                        <div>
                          <LegacyIconButton
                            icon="i-ph:plugs"
                            size="sm"
                            title="Disconnect project"
                            onClick={() => setIsDisconnectDialogOpen(true)}
                          />
                        </div>
                      </div>
                    </div>
                    <hr className="my-1 border-bolt-elements-borderColor" />
                    <DropdownButton onClick={onClickGoToSupabase}>
                      <span className="i-logos:supabase-icon size-4" />
                      Go to Supabase <span className="i-ph:arrow-up-right" />
                    </DropdownButton>
                  </div>

                  <DropdownMenu.Arrow className="fill-bolt-elements-borderColor" />
                </motion.div>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          )}
        </AnimatePresence>
      </DropdownMenu.Root>
      <Dialog open={isDisconnectDialogOpen} maxWidth={500} onClose={() => setIsDisconnectDialogOpen(false)}>
        <DialogTitle>Disconnecting project</DialogTitle>
        <DialogDescription>Are you sure you want to disconnect your project?</DialogDescription>
        <DialogActions>
          <DialogButton variant="secondary" onClick={() => setIsDisconnectDialogOpen(false)}>
            Cancel
          </DialogButton>
          <DialogButton variant="warn" onClick={onDisconnect}>
            Disconnect
          </DialogButton>
        </DialogActions>
      </Dialog>
    </>
  );
});
